import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ApiUrlService } from '../../core/api-url.service';
import { finalize } from 'rxjs';
import { SpwDesignSystemDirectivesModule } from '@spw-ds/spw-angular-library';

@Component({
  selector: 'app-admin-users-page',
  standalone: true,
  imports: [FormsModule, NgFor, NgIf, SpwDesignSystemDirectivesModule],
  templateUrl: './admin-users.page.html',
  styleUrl: './admin-users.page.scss'
})
export class AdminUsersPageComponent implements OnInit {
  users: User[] = [];
  plateaux: Plateau[] = [];
  form: CreateUserPayload = {
    prenom: '',
    nom: '',
    email: '',
    password: '',
    role: 'USER',
    plateauId: null
  };
  successMessage = '';
  errorMessage = '';
  isSubmitting = false;
  isUpdating = false;
  deletingUserId: number | null = null;
  selectedUser: User | null = null;
  editForm: EditUserPayload = {
    prenom: '',
    nom: '',
    email: '',
    password: '',
    role: 'USER',
    plateauId: null,
    active: true
  };

  constructor(
    private readonly http: HttpClient,
    private readonly apiUrl: ApiUrlService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadUsers();
    this.loadPlateaux();
  }

  loadUsers(): void {
    this.http.get<User[]>(this.apiUrl.build('/api/admin/users')).subscribe({
      next: response => {
        this.users = response;
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = 'Impossible de charger les utilisateurs.';
        this.cdr.markForCheck();
      }
    });
  }

  loadPlateaux(): void {
    this.http.get<Plateau[]>(this.apiUrl.build('/api/admin/plateaux')).subscribe({
      next: response => {
        this.plateaux = response;
        if (!this.form.plateauId && response.length > 0) {
          this.form.plateauId = response[0].id;
        }
        this.cdr.markForCheck();
      },
      error: () => {
        this.errorMessage = 'Impossible de charger les plateaux.';
        this.cdr.markForCheck();
      }
    });
  }

  createUser(): void {
    if (!this.form.plateauId) {
      this.errorMessage = 'Veuillez sélectionner un plateau.';
      return;
    }
    this.errorMessage = '';
    this.successMessage = '';
    this.isSubmitting = true;
    this.http
      .post<User>(this.apiUrl.build('/api/admin/users'), { ...this.form })
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.successMessage = 'Utilisateur créé.';
          this.form = {
            prenom: '',
            nom: '',
            email: '',
            password: '',
            role: 'USER',
            plateauId: this.form.plateauId
          };
          this.loadUsers();
          this.cdr.markForCheck();
        },
        error: error => {
          if (error?.status === 409) {
            this.errorMessage = 'Cet email est déjà utilisé.';
            this.cdr.markForCheck();
            return;
          }
          this.errorMessage = 'Erreur lors de la création.';
          this.cdr.markForCheck();
        }
      });
  }

  startEdit(user: User): void {
    this.selectedUser = user;
    this.editForm = {
      prenom: user.prenom,
      nom: user.nom,
      email: user.email,
      password: '',
      role: user.role,
      plateauId: user.plateauId,
      active: user.active
    };
    this.errorMessage = '';
    this.successMessage = '';
  }

  cancelEdit(): void {
    this.selectedUser = null;
  }

  updateUser(): void {
    if (!this.selectedUser) {
      return;
    }
    if (!this.editForm.plateauId) {
      this.errorMessage = 'Veuillez sélectionner un plateau.';
      return;
    }
    if (!this.editForm.password) {
      this.errorMessage = 'Le mot de passe est requis.';
      return;
    }
    this.errorMessage = '';
    this.successMessage = '';
    this.isUpdating = true;
    this.http
      .put<User>(this.apiUrl.build(`/api/admin/users/${this.selectedUser.id}`), { ...this.editForm })
      .pipe(
        finalize(() => {
          this.isUpdating = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.successMessage = 'Utilisateur mis à jour.';
          this.selectedUser = null;
          this.loadUsers();
          this.cdr.markForCheck();
        },
        error: error => {
          if (error?.status === 409) {
            this.errorMessage = 'Cet email est déjà utilisé.';
            this.cdr.markForCheck();
            return;
          }
          this.errorMessage = 'Erreur lors de la mise à jour.';
          this.cdr.markForCheck();
        }
      });
  }

  deleteUser(user: User): void {
    if (!confirm(`Supprimer l'utilisateur ${user.prenom} ${user.nom} ?`)) {
      return;
    }
    this.deletingUserId = user.id;
    this.errorMessage = '';
    this.successMessage = '';
    this.http
      .delete<void>(this.apiUrl.build(`/api/admin/users/${user.id}`))
      .pipe(
        finalize(() => {
          this.deletingUserId = null;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.successMessage = 'Utilisateur supprimé.';
          if (this.selectedUser?.id === user.id) {
            this.selectedUser = null;
          }
          this.loadUsers();
          this.cdr.markForCheck();
        },
        error: () => {
          this.errorMessage = 'Erreur lors de la suppression.';
          this.cdr.markForCheck();
        }
      });
  }

  getPlateauName(plateauId: number): string {
    return this.plateaux.find(plateau => plateau.id === plateauId)?.name ?? String(plateauId);
  }
}

interface User {
  id: number;
  prenom: string;
  nom: string;
  email: string;
  role: 'ADMIN' | 'USER';
  active: boolean;
  plateauId: number;
}

interface Plateau {
  id: number;
  name: string;
}

interface CreateUserPayload {
  prenom: string;
  nom: string;
  email: string;
  password: string;
  role: 'ADMIN' | 'USER';
  plateauId: number | null;
}

interface EditUserPayload {
  prenom: string;
  nom: string;
  email: string;
  password: string;
  role: 'ADMIN' | 'USER';
  plateauId: number | null;
  active: boolean;
}
